import React, { useState, useEffect } from 'react';
import {
  Add as PlusIcon,
  Edit as PencilIcon,
  Delete as TrashIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  Refresh as ArrowPathIcon
} from '@mui/icons-material';
import {
  getSalesPlaybooks,
  createSalesPlaybook,
  deleteSalesPlaybook,
  toggleSalesPlaybookStatus,
  bulkUpdateSalesPlaybookRoles,
  type SalesPlaybookIndustry,
  type SalesPlaybookTag
} from '../services/apiService';
import AddSalesPlaybookModal from './AddSalesPlaybookModal';
import PlaybookLibraryManagement from './PlaybookLibraryManagement';

const SALES_PLAYBOOK_TAG_LABELS: Record<SalesPlaybookTag, string> = {
  situation: 'Situation',
  pain: 'Pain',
  cost_math: 'Cost Math',
  authority: 'Authority',
  timeline: 'Timeline',
  close_trigger: 'Close Trigger'
};

interface SalesPlaybookManagementProps {
  onRefresh?: () => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

const SalesPlaybookManagement: React.FC<SalesPlaybookManagementProps> = ({
  setError,
  setSuccess
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'playbooks' | 'library'>('playbooks');
  const [industries, setIndustries] = useState<SalesPlaybookIndustry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndustryKey, setSelectedIndustryKey] = useState<string>('');
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());

  // Modal states
  const [isAddSalesPlaybookModalOpen, setIsAddSalesPlaybookModalOpen] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState<SalesPlaybookIndustry | null>(null);

  const fetchIndustries = async () => {
    setLoading(true);
    try {
      const response = await getSalesPlaybooks(1, 100);
      const fetchedIndustries = response.salesPlaybooks || [];
      setIndustries(fetchedIndustries);

      // Auto-select first industry if none selected and industries exist
      if (!selectedIndustryKey && fetchedIndustries.length > 0) {
        setSelectedIndustryKey(fetchedIndustries[0].industry);
        setExpandedRoles(new Set(fetchedIndustries[0].roles.map(r => r._id)));
      }
    } catch (error) {
      console.error('Error fetching sales playbook:', error);
      setError('Failed to load sales playbook');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIndustries();
  }, []);

  // Get selected industry object
  const selectedIndustry = industries.find(i => i.industry === selectedIndustryKey) || null;

  const handleIndustryChange = (industryKey: string) => {
    setSelectedIndustryKey(industryKey);
    const industry = industries.find(i => i.industry === industryKey);
    if (industry) {
      setExpandedRoles(new Set(industry.roles.map(r => r._id)));
    }
  };

  const toggleRoleExpanded = (roleId: string) => {
    const newExpanded = new Set(expandedRoles);
    if (newExpanded.has(roleId)) {
      newExpanded.delete(roleId);
    } else {
      newExpanded.add(roleId);
    }
    setExpandedRoles(newExpanded);
  };

  const handleSaveSalesPlaybook = async (data: {
    industryDisplayName: string;
    roles: {
      originalId?: string;
      displayName: string;
      pitchBullets: string[];
      advancedBullets: string[];
      questions: { text: string; tags: SalesPlaybookTag[] }[];
    }[];
  }) => {
    try {
      const isEditing = !!editingIndustry;

      // Check if industry exists
      let existingIndustry = industries.find(
        i => i.displayName.toLowerCase() === data.industryDisplayName.toLowerCase()
      );

      // Create industry if it doesn't exist
      let currentIndustry: SalesPlaybookIndustry;
      if (!existingIndustry) {
        currentIndustry = await createSalesPlaybook(data.industryDisplayName);
        setSuccess(`Industry "${data.industryDisplayName}" created`);
      } else {
        currentIndustry = existingIndustry;
      }

      // Use single bulk API call to update all roles at once
      await bulkUpdateSalesPlaybookRoles(currentIndustry.industry, data.roles);

      setSuccess(isEditing ? 'Sales playbook updated successfully' : 'Sales playbook added successfully');
      setIsAddSalesPlaybookModalOpen(false);
      setEditingIndustry(null);
      await fetchIndustries();

      // Select the newly added/updated industry
      setSelectedIndustryKey(currentIndustry.industry);
    } catch (error: any) {
      setError(error.message || 'Failed to save sales playbook');
    }
  };

  const handleDeleteIndustry = async (industry: SalesPlaybookIndustry) => {
    if (!window.confirm(`Are you sure you want to delete "${industry.displayName}"? This will delete all roles, pitch bullets, and questions.`)) {
      return;
    }

    try {
      await deleteSalesPlaybook(industry.industry);
      setSuccess('Sales playbook deleted successfully.');
      if (selectedIndustryKey === industry.industry) {
        setSelectedIndustryKey('');
      }
      await fetchIndustries();
    } catch (error: any) {
      setError(error.message || 'Failed to delete industry');
    }
  };

  const handleToggleStatus = async (industry: SalesPlaybookIndustry) => {
    try {
      await toggleSalesPlaybookStatus(industry.industry);
      setSuccess(`${industry.displayName} status updated`);
      await fetchIndustries();
    } catch (error: any) {
      setError(error.message || 'Failed to toggle status');
    }
  };

  const handleEditIndustry = (industry: SalesPlaybookIndustry) => {
    setEditingIndustry(industry);
    setIsAddSalesPlaybookModalOpen(true);
  };

  // Return all roles - no filtering
  const getDisplayRoles = (industry: SalesPlaybookIndustry) => {
    return industry.roles;
  };

  return (
    <div className="flex flex-col h-full relative">
      {/* Refresh overlay */}
      {loading && industries.length > 0 && activeSubTab === 'playbooks' && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
          <div className="flex flex-col items-center gap-2">
            <ArrowPathIcon className="animate-spin text-blue-600" style={{ fontSize: '2.5rem' }} />
            <span className="text-sm font-medium text-gray-600">Refreshing...</span>
          </div>
        </div>
      )}

      {/* Sub-tab navigation */}
      <div className="flex border-b border-gray-200 bg-white px-4">
        <button
          onClick={() => setActiveSubTab('playbooks')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeSubTab === 'playbooks'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Playbooks
        </button>
        <button
          onClick={() => setActiveSubTab('library')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeSubTab === 'library'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Library
        </button>
      </div>

      {activeSubTab === 'library' ? (
        <PlaybookLibraryManagement setError={setError} setSuccess={setSuccess} />
      ) : (
      <>
      {/* Description */}
      <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
        <p className="text-sm text-blue-800">
          <span className="font-medium">How it works:</span> Create playbooks by industry (e.g., "Cleaning"), then add roles within each industry (e.g., "Facility Manager", "Procurement"). Each role has its own intro pitch, qualifying questions, and deal progression talking points. During a call, salespeople select their industry and role to get tailored guidance.
        </p>
      </div>

      {/* Header with Industry Dropdown and Add Button */}
      <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <label className="text-sm font-medium text-gray-700">Industry:</label>
          <select
            value={selectedIndustryKey}
            onChange={(e) => handleIndustryChange(e.target.value)}
            className="flex-1 max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
          >
            <option value="">Select an industry...</option>
            {industries.map(industry => (
              <option key={industry.industry} value={industry.industry}>
                {industry.displayName}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchIndustries}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            title="Refresh"
            disabled={loading}
          >
            <ArrowPathIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: '1.25rem' }} />
          </button>
          <button
            onClick={() => {
              setEditingIndustry(null);
              setIsAddSalesPlaybookModalOpen(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <PlusIcon style={{ fontSize: '1.25rem' }} />
            Add Sales Playbook
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        {loading && industries.length === 0 ? (
          <div className="p-8 text-center">
            <ArrowPathIcon className="animate-spin mx-auto text-gray-400" style={{ fontSize: '2rem' }} />
            <p className="mt-2 text-gray-500">Loading sales playbook...</p>
          </div>
        ) : !selectedIndustry ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg mb-2">
              {industries.length === 0 ? 'No sales playbook yet' : 'Select an industry to view sales playbook'}
            </p>
            <p className="text-sm">
              Click "Add Sales Playbook" to create one with industry, role, pitch, and questions.
            </p>
          </div>
        ) : (
          <div className="p-4">
            {/* Industry Header */}
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{selectedIndustry.displayName}</h2>
                <p className="text-sm text-gray-500">
                  {getDisplayRoles(selectedIndustry).length} role{getDisplayRoles(selectedIndustry).length !== 1 ? 's' : ''} | {selectedIndustry.roles.reduce((sum, r) => sum + r.questions.length, 0)} total questions
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleStatus(selectedIndustry)}
                  className={`px-3 py-1.5 rounded-lg text-sm ${
                    selectedIndustry.isActive
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                >
                  {selectedIndustry.isActive ? (
                    <><CheckCircleIcon style={{ fontSize: '1rem' }} className="mr-1" />Active</>
                  ) : (
                    <><XCircleIcon style={{ fontSize: '1rem' }} className="mr-1" />Inactive</>
                  )}
                </button>
                <button
                  onClick={() => handleEditIndustry(selectedIndustry)}
                  className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm flex items-center gap-1"
                >
                  <PencilIcon style={{ fontSize: '1rem' }} />
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteIndustry(selectedIndustry)}
                  className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                  title="Delete Industry"
                >
                  <TrashIcon style={{ fontSize: '1rem' }} />
                </button>
              </div>
            </div>

            {/* Roles List - Read Only View */}
            <div className="space-y-2">
              {getDisplayRoles(selectedIndustry).map(role => (
                <div key={role._id} className="border border-gray-200 rounded-lg bg-white shadow-sm">
                  {/* Role Header */}
                  <div
                    className="px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleRoleExpanded(role._id)}
                  >
                    <div className="flex items-center gap-2">
                      {expandedRoles.has(role._id) ? (
                        <ExpandLessIcon className="text-gray-400" />
                      ) : (
                        <ExpandMoreIcon className="text-gray-400" />
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900 uppercase">{role.displayName}</h3>
                        <p className="text-sm text-gray-500">
                          {role.pitch.length} pitch{role.pitch.length !== 1 ? 'es' : ''}, {role.questions.length} question{role.questions.length !== 1 ? 's' : ''}, {role.advanced?.length || 0} deal progression
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Role Content (Expanded) - Read Only - 3 Column Layout */}
                  {expandedRoles.has(role._id) && (
                    <div className="border-t border-gray-200 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Intro Pitch Section */}
                        <div className="bg-gray-50 rounded-lg p-3 md:border-r md:border-gray-200 md:rounded-r-none md:pr-4">
                          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                            Intro Pitch
                          </h4>
                          {role.pitch.length === 0 ? (
                            <p className="text-sm text-gray-400 italic">No pitch bullets</p>
                          ) : (
                            <ul className="space-y-1">
                              {role.pitch
                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                .map(pitch => (
                                  <li key={pitch._id} className="flex items-start gap-2">
                                    <span className="text-gray-400 mt-0.5">-</span>
                                    <span className="text-sm text-gray-700">{pitch.text}</span>
                                  </li>
                                ))}
                            </ul>
                          )}
                        </div>

                        {/* Qualifying Questions Section */}
                        <div className="bg-gray-50 rounded-lg p-3 md:border-r md:border-gray-200 md:rounded-none md:px-4">
                          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                            Qualifying Questions
                          </h4>
                          {role.questions.length === 0 ? (
                            <p className="text-sm text-gray-400 italic">No questions</p>
                          ) : (
                            <ul className="space-y-2">
                              {role.questions.map(q => (
                                <li key={q._id} className="flex items-start gap-2">
                                  <span className="text-gray-400 mt-0.5">-</span>
                                  <div className="flex-1">
                                    <span className="text-sm text-gray-700">{q.questionText}</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {q.tags.map(tag => (
                                        <span
                                          key={tag}
                                          className="px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700"
                                        >
                                          {SALES_PLAYBOOK_TAG_LABELS[tag]}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {/* Deal Progression Section */}
                        <div className="bg-gray-50 rounded-lg p-3 md:rounded-l-none md:pl-4">
                          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                            Deal Progression
                          </h4>
                          {(!role.advanced || role.advanced.length === 0) ? (
                            <p className="text-sm text-gray-400 italic">No deal progression items</p>
                          ) : (
                            <ul className="space-y-1">
                              {role.advanced
                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                .map(item => (
                                  <li key={item._id} className="flex items-start gap-2">
                                    <span className="text-gray-400 mt-0.5">-</span>
                                    <span className="text-sm text-gray-700">{item.text}</span>
                                  </li>
                                ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      </>
      )}

      {/* Modal */}
      <AddSalesPlaybookModal
        isOpen={isAddSalesPlaybookModalOpen}
        onClose={() => {
          setIsAddSalesPlaybookModalOpen(false);
          setEditingIndustry(null);
        }}
        onSave={handleSaveSalesPlaybook}
        existingIndustries={industries.map(i => i.displayName)}
        editingIndustry={editingIndustry}
      />
    </div>
  );
};

export default SalesPlaybookManagement;
